// The SimpleShapes target style: four outline shapes in a 2x2 grid, in the same reading order as
// optionIndex's "Option_1".."Option_4" (top left, top right, bottom left, bottom right). The catch
// ("none stood out") target sits apart, below the grid, where the "?" is printed.
const POSITIONS = [
  { x: -25, y: -25 },
  { x: 25, y: -25 },
  { x: -25, y: 25 },
  { x: 25, y: 25 },
];
const CATCH_POSITION = { x: 0, y: 72.57 };
const CATCH_INDEX = 4;

const SHAPE_SIZE = 29.7;
const STROKE = 3.3;
const RING_SIZE = 44;
const CATCH_RING_SIZE = 26.74;

// An equilateral triangle centred on its outline rather than its centroid, so it sits level with
// the circle and square beside it. The miter at the apex pokes out a full stroke width past the
// point, while the base only carries half a stroke below it, so the points shift up by the
// difference to balance the drawn shape.
const TRIANGLE_HALF_BASE = 13.642;
const TRIANGLE_HEIGHT = TRIANGLE_HALF_BASE * Math.sqrt(3);
const TRIANGLE_OFFSET = (STROKE - STROKE / 2) / 2;
const TRIANGLE_APEX = TRIANGLE_HEIGHT / 2 - TRIANGLE_OFFSET;
const TRIANGLE_BASE = TRIANGLE_HEIGHT / 2 + TRIANGLE_OFFSET;

const GOOD = "#22BE39";
const BAD = "#E10C0C";
const BACKGROUND = "#808080";
const PICTURE_HEIGHT = "225px";
const SHAPE_COLOR = "#FFFFFF";
const FONT_FAMILY = "'Gill Sans MT', 'Gill Sans', Calibri, sans-serif";

const positionFor = (index: number) =>
  index === CATCH_INDEX ? CATCH_POSITION : POSITIONS[index] ?? null;

const ringSizeFor = (index: number) =>
  index === CATCH_INDEX ? CATCH_RING_SIZE : RING_SIZE;

const Shape = ({ shape, x, y }: { shape: string; x: number; y: number }) => {
  const half = SHAPE_SIZE / 2;

  switch (shape) {
    case "Square":
      return (
        <rect
          x={x - half}
          y={y - half}
          width={SHAPE_SIZE}
          height={SHAPE_SIZE}
          fill="none"
          stroke={SHAPE_COLOR}
          strokeWidth={STROKE}
        />
      );
    case "Cross":
      return (
        <>
          <rect
            x={x - 16.5}
            y={y - STROKE / 2}
            width={33}
            height={STROKE}
            fill={SHAPE_COLOR}
          />
          <rect
            x={x - STROKE / 2}
            y={y - 16.5}
            width={STROKE}
            height={33}
            fill={SHAPE_COLOR}
          />
        </>
      );
    case "Triangle":
      return (
        <polygon
          points={`${x},${y - TRIANGLE_APEX} ${x + TRIANGLE_HALF_BASE},${y + TRIANGLE_BASE} ${x - TRIANGLE_HALF_BASE},${y + TRIANGLE_BASE}`}
          fill="none"
          stroke={SHAPE_COLOR}
          strokeWidth={STROKE}
          strokeLinejoin="miter"
        />
      );
    case "Circle":
      return (
        <circle
          cx={x}
          cy={y}
          r={half}
          fill="none"
          stroke={SHAPE_COLOR}
          strokeWidth={STROKE}
        />
      );
    default:
      return null;
  }
};

const Ring = ({
  index,
  color,
  dashed,
}: {
  index: number;
  color: string;
  dashed?: boolean;
}) => {
  const position = positionFor(index);
  if (!position) return null;

  // The dashed answer ring sits a hair outside the solid pick ring so both read when they overlap.
  const size = ringSizeFor(index) + (dashed && index !== CATCH_INDEX ? 0.6 : 0);
  return (
    <rect
      x={position.x - size / 2}
      y={position.y - size / 2}
      width={size}
      height={size}
      fill="none"
      stroke={color}
      strokeWidth={dashed ? 2.4 : 3}
      strokeDasharray={dashed ? "4 3" : undefined}
    />
  );
};

type ShapesTrialPictureType = {
  shapes: Array<string>;
  expectedIndex: number;
  responseIndex: number;
  correct: boolean;
};

// As with the circles, which shape had stereo depth can't be shown in a flat image, so all four
// render identically. The squares marking the answer/pick are this review tool's own annotations.
const ShapesTrialPicture = ({
  shapes,
  expectedIndex,
  responseIndex,
  correct,
}: ShapesTrialPictureType) => (
  <svg
    viewBox="-60 -60 120 153.14"
    role="img"
    aria-label={`Shapes: ${shapes.join(", ")}`}
    // A fixed height so circle and shape trials line up; the background is painted on the svg
    // itself so it fills the full box even where the drawing letterboxes inside it.
    style={{
      display: "block",
      width: "100%",
      height: PICTURE_HEIGHT,
      backgroundColor: BACKGROUND,
      borderRadius: "8px",
    }}
  >
    {POSITIONS.map(({ x, y }, index) => (
      <Shape key={index} shape={shapes[index]} x={x} y={y} />
    ))}

    <text
      x={CATCH_POSITION.x}
      y={CATCH_POSITION.y}
      fontSize={15.226}
      fontWeight={600}
      fill={SHAPE_COLOR}
      textAnchor="middle"
      dominantBaseline="central"
      fontFamily={FONT_FAMILY}
    >
      ?
    </text>

    {correct ? (
      <Ring index={responseIndex} color={GOOD} />
    ) : (
      <>
        <Ring index={expectedIndex} color={GOOD} dashed />
        <Ring index={responseIndex} color={BAD} />
      </>
    )}
  </svg>
);

export default ShapesTrialPicture;
